import { createContext, useContext } from 'react'

import type { PostcodeLookupResult } from '@/shared/lib/postcode-lookup'

import type { AddressSuggestion, AddressValue } from './address-input'

// ---------------------------------------------------------------------------
// Lookup context - lets admin code provide lookup functions without coupling
// the shared AddressInput component to admin-specific API instances.
// ---------------------------------------------------------------------------

/**
 * What the address-search UI needs to render its country picker. Sourced from
 * the org's ADDRESS integration settings (see the backend
 * ``/lookup/address/capabilities/`` endpoint). ``countries`` are ISO alpha-2
 * codes, default first; the picker shows only when more than one is offered.
 */
export interface AddressLookupCapabilities {
  enabled: boolean
  defaultCountry: string
  countries: string[]
}

export interface AddressLookupAPI {
  /**
   * Fetch autocomplete suggestions for a search term. ``country`` optionally
   * biases results to an ISO alpha-2 country (from the search country picker);
   * omit for the org's default country. A suggestion may be a *group*
   * (``entries > 1``) - see ``expand``.
   */
  autocomplete: (term: string, country?: string) => Promise<AddressSuggestion[]>
  /** Resolve a suggestion into a full address. */
  resolve: (id: string, term: string) => Promise<AddressValue>
  /**
   * Expand a *group* suggestion (``entries > 1`` - e.g. an apartment building
   * that stands for many units) into its member suggestions, each directly
   * resolvable. Optional: providers/surfaces without groups omit it.
   */
  expand?: (id: string) => Promise<AddressSuggestion[]>
  /**
   * The countries the search offers + the default. Optional: a surface that has
   * not wired it up simply shows no country picker (single-country behaviour,
   * exactly as before).
   */
  capabilities?: () => Promise<AddressLookupCapabilities>
  /**
   * Resolve a postal code to its valid cities (USPS default / primary first)
   * from the platform reference table. Returns ``[]`` for an unknown code so
   * the caller leaves a typed city untouched instead of guessing. Only US is
   * populated today; non-US postcodes resolve offline via ``lookupPostcode``.
   */
  lookupPostcode: (
    postcode: string,
    country?: string,
  ) => Promise<PostcodeLookupResult[]>
}

const AddressLookupContext = createContext<AddressLookupAPI | null>(null)

/** Wrap a form tree to provide address lookup capabilities. */
export function AddressLookupProvider({
  api,
  children,
}: {
  api: AddressLookupAPI
  children: React.ReactNode
}) {
  return (
    <AddressLookupContext.Provider value={api}>
      {children}
    </AddressLookupContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAddressLookup(): AddressLookupAPI | null {
  return useContext(AddressLookupContext)
}
